import { Table, Dropdown, Popconfirm } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import moment from 'moment';
import React, { useState } from 'react';
import { DAYTIME_FORMAT } from '../../../constants';
import { ReactSVG } from 'react-svg';

const STATUS_COLORS = {
  pending: { color: '#d46b08', fontWeight: 600 },
  approved: { color: '#389e0d', fontWeight: 600 },
  rejected: { color: '#cf1322', fontWeight: 600 },
  locked: { color: '#595959', fontWeight: 600 },
};

const cellEllipsisStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  display: 'block',
};

const EllipsisCell = ({ value, children }) => {
  const text = value ?? children ?? '';
  return (
    <div title={text} style={cellEllipsisStyle}>
      {children ?? value ?? ''}
    </div>
  );
};

const StatusCell = ({ row, onApproveDriver, onRejectDriver, onLockDriver }) => {
  const [open, setOpen] = useState(false);
  const approvalStatus = row?.approvalStatus?.toLowerCase() || (row?.user?.isActive ? 'approved' : 'pending');
  const isPending = approvalStatus === 'pending';
  const isApproved = approvalStatus === 'approved';
  const isRejected = approvalStatus === 'rejected';
  const isLocked = approvalStatus === 'locked';
  const statusLabel = isApproved ? 'Approved' : isRejected ? 'Rejected' : isLocked ? 'Locked' : 'Pending';
  const textStyle = STATUS_COLORS[approvalStatus] || STATUS_COLORS.pending;
  const hasActions =
    (isPending && (onApproveDriver || onRejectDriver)) ||
    (isRejected && onApproveDriver) ||
    (isApproved && onLockDriver) ||
    (isLocked && onApproveDriver);

  const overlay = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 100,
        background: '#fff',
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        padding: '4px 0',
      }}
    >
      {(isPending || isRejected || isLocked) && (
        <div
          role="button"
          tabIndex={0}
          style={{
            padding: '6px 12px',
            cursor: 'pointer',
            color: '#389e0d',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onApproveDriver(row.id);
            setOpen(false);
          }}
          onKeyDown={(e) => e.key === 'Enter' && onApproveDriver(row.id)}
        >
          Approved
        </div>
      )}
      {isPending && (
        <div
          role="button"
          tabIndex={0}
          style={{
            padding: '6px 12px',
            cursor: 'pointer',
            color: '#cf1322',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRejectDriver(row.id);
            setOpen(false);
          }}
          onKeyDown={(e) => e.key === 'Enter' && onRejectDriver(row.id)}
        >
          Rejected
        </div>
      )}
      {isApproved && onLockDriver && (
        <Popconfirm
          title="Lock this driver?"
          description="The driver account will be temporarily locked pending document verification."
          onConfirm={() => {
            onLockDriver(row.id);
            setOpen(false);
          }}
          okText="Lock"
          cancelText="Cancel"
        >
          <div
            role="button"
            tabIndex={0}
            style={{
              padding: '6px 12px',
              cursor: 'pointer',
              color: '#595959',
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Enter' && e.target.click()}
          >
            Locked
          </div>
        </Popconfirm>
      )}
    </div>
  );

  const triggerNode = (
    <span className="status users" style={{ ...textStyle, cursor: hasActions ? 'pointer' : 'default' }}>
      {statusLabel}
    </span>
  );

  if (!hasActions) {
    return triggerNode;
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['hover']}
      dropdownRender={() => overlay}
      placement="bottomLeft"
    >
      <div style={{ minHeight: 32, display: 'inline-block' }}>{triggerNode}</div>
    </Dropdown>
  );
};

const DriversTable = ({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  onApproveDriver,
  onRejectDriver,
  onLockDriver,
  onTogglePreferred,
  onToggleGstPayout,
  ...props
}) => {
  const columns = [
    {
      title: 'Driver ID',
      key: 'internalId',
      fixed: 'left',
      width: 150,
      render: (row) => {
        const internalId = row?.user?.internalId || row?.driverId;
        return <EllipsisCell value={internalId}>{internalId ?? ''}</EllipsisCell>;
      },
    },
    {
      title: 'Driver Name',
      key: 'name',
      fixed: 'left',
      width: 180,
      render: (row) => {
        const name = row?.user?.firstName
          ? row?.user?.firstName + ' ' + (row?.user?.lastName ?? '')
          : row?.user?.name ?? '';
        return <EllipsisCell value={name}>{name}</EllipsisCell>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (value) => {
        const formatted = value ? moment(value).format(DAYTIME_FORMAT) : '';
        return <EllipsisCell value={formatted}>{formatted}</EllipsisCell>;
      },
    },
    {
      title: 'Email',
      key: 'email',
      width: 280,
      render: (row) => {
        const email = row?.user?.email || '';
        return <EllipsisCell value={email}>{email}</EllipsisCell>;
      },
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 140,
      render: (row) => {
        const phone = row?.user?.phone || '';
        return <EllipsisCell value={phone}>{phone}</EllipsisCell>;
      },
    },
    {
      title: 'Vehicle Model',
      dataIndex: 'vehicleModel',
      key: 'vehicleModel',
      width: 140,
      render: (value) => <EllipsisCell value={value}>{value ?? ''}</EllipsisCell>,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (value) => {
        const formatted = value ? moment(value).format(DAYTIME_FORMAT) : '';
        return <EllipsisCell value={formatted}>{formatted}</EllipsisCell>;
      },
    },
    {
      title: 'Status',
      fixed: 'right',
      width: 100,
      key: 6,
      render: (row) =>
        row.user?.email?.endsWith('@deleted.com') ? (
          <span className="status users locked">Locked</span>
        ) : (
          <StatusCell
            row={row}
            onApproveDriver={onApproveDriver}
            onRejectDriver={onRejectDriver}
            onLockDriver={onLockDriver}
          />
        ),
    },
    {
      title: 'Preferred',
      key: 'isPreferred',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (row) => {
        if (row.user?.email?.endsWith('@deleted.com')) {
          return <CloseCircleFilled style={{ color: '#bfbfbf', fontSize: 18, cursor: 'not-allowed' }} />;
        }
        const approvalStatus = row?.approvalStatus?.toLowerCase() || (row?.user?.isActive ? 'approved' : 'pending');
        const isApproved = approvalStatus === 'approved';
        const isPreferred = isApproved && row.isPreferred;
        if (isPreferred) {
          return (
            <CheckCircleFilled
              style={{ color: '#389e0d', fontSize: 18, cursor: 'pointer' }}
              onClick={() => onTogglePreferred && onTogglePreferred(row.id, false)}
            />
          );
        }
        return (
          <CloseCircleFilled
            style={{ color: '#bfbfbf', fontSize: 18, cursor: isApproved ? 'pointer' : 'not-allowed' }}
            onClick={() => isApproved && onTogglePreferred && onTogglePreferred(row.id, true)}
          />
        );
      },
    },
    {
      title: 'Test GST Payout',
      key: 'gstPayoutEnabled',
      width: 110,
      fixed: 'right',
      align: 'center',
      render: (row) => {
        if (row.user?.email?.endsWith('@deleted.com')) {
          return <CloseCircleFilled style={{ color: '#bfbfbf', fontSize: 18, cursor: 'not-allowed' }} />;
        }
        if (row.gstPayoutEnabled) {
          return (
            <CheckCircleFilled
              style={{ color: '#389e0d', fontSize: 18, cursor: 'pointer' }}
              onClick={() => onToggleGstPayout && onToggleGstPayout(row.id, false)}
            />
          );
        }
        return (
          <CloseCircleFilled
            style={{ color: '#bfbfbf', fontSize: 18, cursor: 'pointer' }}
            onClick={() => onToggleGstPayout && onToggleGstPayout(row.id, true)}
          />
        );
      },
    },
    {
      key: 'star',
      width: 60,
      fixed: 'right',
      render(row) {
        if (!row.user.avgRating) return;
        return (
          <div className="star-rating">
            <span className="rating-value">{row.user.avgRating}</span>
            <ReactSVG className="star-icon" src="/icons/star.svg" />
          </div>
        );
      },
    },
  ];

  if (withAction) {
    columns.push({
      key: 'action',
      width: 120,
      fixed: 'right',
      render(record) {
        return withAction(record);
      },
    });
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const scrollX = 1350;

  return (
    <div className="table-users table-common table-responsive-wrapper">
      <Table
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ x: scrollX }}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
    </div>
  );
};

export default DriversTable;
